import { useState } from 'react';
import type { CalibreData } from '../../services/firebase';
import { countOverrides } from './calibreTiers';
import { parseMultiLookupValues } from '../../utils/multiLookupValues';

/**
 * Read-only "Full calibre detail" panel for the ref editor's linked-calibre
 * area. Supplements {@link RefCalibreCachePanel} (which renders the tier-EFFECTIVE
 * spec for the cached/overridable field set) by showing the calibre fields that
 * panel doesn't carry: construction detail, decoration & finishing (the v1.39.0
 * fields), and a read-only tiers summary. Sourced straight from the linked
 * calibre's full `CalibreData` (`activeLinkedCalibre.data`) — these fields are
 * never per-ref overridden, so the base calibre value is authoritative.
 *
 * Display-only: no edit surface, no override controls. Rows are omitted entirely
 * when their value is null/undefined/empty/false so the panel stays terse. Every
 * field access is null-guarded (all `CalibreData` fields are optional).
 */

type FieldKind = 'text' | 'number' | 'lookup' | 'multi-lookup' | 'boolean';

interface DetailField {
  key: keyof CalibreData;
  label: string;
  kind: FieldKind;
  /** Lookup collection whose `displayName` replaces the raw slug (the `lookup`
   *  and `multi-lookup` kinds; the latter resolves every member of the array). */
  lookup?: string;
}

/** Sections mirroring the calibre editor's field groups, scan-only. Identity /
 *  Specs / Certifications / Complications already live in RefCalibreCachePanel;
 *  these are the remaining groups. */
const DETAIL_SECTIONS: { title: string; fields: DetailField[] }[] = [
  {
    title: 'Construction',
    fields: [
      { key: 'movementArchitecture', label: 'Architecture', kind: 'lookup', lookup: 'lookup_movement_architecture' },
      { key: 'balanceType', label: 'Balance type', kind: 'multi-lookup', lookup: 'lookup_balance_type' },
      { key: 'numberOfBridges', label: 'Bridges', kind: 'number' },
      { key: 'barrelCount', label: 'Barrels', kind: 'number' },
      { key: 'liftAngleDegrees', label: 'Lift angle (°)', kind: 'number' },
      { key: 'casingDiameterMm', label: 'Casing diameter (mm)', kind: 'number' },
      { key: 'lignes', label: 'Lignes', kind: 'text' },
      { key: 'serviceIntervalYears', label: 'Service interval (y)', kind: 'number' },
      { key: 'manualWindingDirection', label: 'Manual winding direction', kind: 'lookup', lookup: 'lookup_winding_directions' },
    ],
  },
  {
    // v1.92.0. What the movement's parts are MADE OF, plus the mainplate's
    // defining surface treatment — kept apart from "Decoration & finishing"
    // below, which carries the overall finishing GRADE and the decorative
    // techniques. `caseMaterialsAvailable` is availability rather than
    // composition (which case metals this calibre is sold in), but it reads
    // naturally alongside the two component materials.
    title: 'Materials & finishing',
    fields: [
      { key: 'rotorMaterial', label: 'Rotor material', kind: 'lookup', lookup: 'lookup_rotor_material' },
      { key: 'bridgeMaterial', label: 'Bridge material', kind: 'lookup', lookup: 'lookup_bridge_material' },
      { key: 'plateFinishing', label: 'Plate finishing', kind: 'lookup', lookup: 'lookup_plate_finishing' },
      { key: 'caseMaterialsAvailable', label: 'Case materials available', kind: 'multi-lookup', lookup: 'lookup_case_materials' },
    ],
  },
  {
    title: 'Decoration & finishing',
    fields: [
      { key: 'skeletonization', label: 'Skeletonization', kind: 'text' },
      { key: 'movementFinishing', label: 'Finishing', kind: 'lookup', lookup: 'lookup_movement_finishing' },
      { key: 'rhodiumPlated', label: 'Rhodium plated', kind: 'boolean' },
      { key: 'giltFinish', label: 'Gilt finish', kind: 'boolean' },
    ],
  },
];

/** Title-case a short enum-ish token (e.g. `partial` → `Partial`). */
function titleCase(v: string): string {
  return v.length ? v[0].toUpperCase() + v.slice(1) : v;
}

function renderFieldValue(
  data: CalibreData,
  field: DetailField,
  resolveLookupLabel: (collection: string, raw: string) => string,
): string | null {
  const v = data[field.key];
  if (v === undefined || v === null || v === '') return null;
  if (field.kind === 'boolean') return v ? '✓' : null;
  if (field.kind === 'number') return typeof v === 'number' ? String(v) : null;
  if (field.kind === 'multi-lookup') {
    // Resolve every member to its label. `parseMultiLookupValues` also carries
    // the pre-v1.91.0 scalar shape (`balanceType: 'glucydur'`), which survives
    // on any calibre the migration hasn't swept yet. An empty result reads as
    // "no value" and drops the row, matching every other kind here.
    const members = parseMultiLookupValues(v);
    if (members.length === 0) return null;
    return members.map((m) => resolveLookupLabel(field.lookup as string, m)).join(', ');
  }
  if (field.kind === 'lookup' && typeof v === 'string') {
    return resolveLookupLabel(field.lookup as string, v);
  }
  if (typeof v === 'string') return titleCase(v);
  return String(v);
}

export function CalibreSpecDetail({
  data,
  resolveLookupLabel,
}: {
  data: CalibreData | undefined;
  resolveLookupLabel: (collection: string, raw: string) => string;
}) {
  const [open, setOpen] = useState(false);

  if (!data) return null;

  // Pre-compute which sections have any present rows.
  const sections = DETAIL_SECTIONS.map((section) => ({
    title: section.title,
    rows: section.fields
      .map((f) => ({ label: f.label, value: renderFieldValue(data, f, resolveLookupLabel) }))
      .filter((r): r is { label: string; value: string } => r.value !== null),
  })).filter((s) => s.rows.length > 0);

  const decorations = (data.decorations ?? []).filter((d) => d && d.trim() !== '');
  const tiers = data.tiers ?? [];

  // Nothing to show → render nothing (keeps the linked area clean).
  if (sections.length === 0 && decorations.length === 0 && tiers.length === 0) {
    return null;
  }

  return (
    <div className='mt-3'>
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        className='flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left'
      >
        <span className='text-xs font-medium text-gray-500 uppercase'>
          Full calibre detail
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {open && (
        <div className='mt-2 rounded-lg border border-gray-200 bg-gray-50/60 p-4 space-y-3'>
          {sections.map((section) => (
            <div key={section.title}>
              <div className='text-[10px] font-semibold text-gray-400 uppercase mb-1'>
                {section.title}
              </div>
              <div className='grid grid-cols-2 gap-x-3 gap-y-1'>
                {section.rows.map((row) => (
                  <div key={row.label} className='flex items-center gap-1.5 text-xs'>
                    <span className='text-gray-500 truncate'>{row.label}</span>
                    <span className='ml-auto font-medium text-gray-700 text-right'>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {decorations.length > 0 && (
            <div>
              <div className='text-[10px] font-semibold text-gray-400 uppercase mb-1'>
                Decorations
              </div>
              <div className='flex flex-wrap gap-1'>
                {decorations.map((slug) => (
                  <span
                    key={slug}
                    className='rounded-full bg-gray-200 text-gray-700 px-2 py-0.5 text-[11px] font-medium'
                  >
                    {resolveLookupLabel('lookup_movement_decoration', slug)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {tiers.length > 0 && (
            <div>
              <div className='text-[10px] font-semibold text-gray-400 uppercase mb-1'>
                Tiers
              </div>
              <div className='space-y-1.5'>
                {tiers.map((tier) => {
                  const n = countOverrides(tier);
                  return (
                    <div key={tier.id} className='text-xs'>
                      <div className='flex items-center gap-1.5'>
                        <span className='font-semibold text-gray-700'>{tier.name}</span>
                        {tier.tierType === 'brand-specific' && (
                          <span className='rounded bg-amber-100 text-amber-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                            brand
                          </span>
                        )}
                        <span className='ml-auto text-gray-400 text-[11px]'>
                          {n} {n === 1 ? 'override' : 'overrides'}
                        </span>
                      </div>
                      {tier.description && (
                        <div className='text-gray-500 mt-0.5'>{tier.description}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
